import { historySummary } from "../helpers/teamAnalysis";
import React, { useState, useEffect } from "react";
import {
  performanceComparison,
  matchPerformance,
} from "../helpers/teamPerformance";
const number = (n, en, d = 2) =>
  Number.isFinite(n)
    ? n.toLocaleString(en ? "en-GB" : "de-DE", {
        minimumFractionDigits: d,
        maximumFractionDigits: d,
      })
    : "—";
export function TeamMatches({ games, teamId, en = false, model = "result" }) {
  const t = (d, e) => (en ? e : d);
  return (
    <div className="ta-scroll">
      <table className="ta-match-list">
        <thead>
          <tr>
            <th>{t("S/U/N", "W/D/L")}</th>
            <th>{t("Paarung", "Match")}</th>
            <th>{t("Ergebnis", "Result")}</th>
            <th>
              {t(
                "Leistung − Erwartung · Tordifferenz",
                "Performance − expectation · goal difference"
              )}
            </th>
          </tr>
        </thead>
        <tbody>
          {games.map((g) => {
            const p = matchPerformance(g, teamId, model);
            return (
              <tr key={g.id}>
                <td>
                  <b className={"ta-outcome ta-outcome-" + p.result}>
                    {en
                      ? { S: "W", U: "D", N: "L" }[p.result]
                      : p.result || "—"}
                  </b>
                </td>
                <td>
                  {["home", "away"].map((side, i) => (
                    <React.Fragment key={side}>
                      {i ? " − " : ""}
                      <span
                        className={
                          Number(g[side + "_team_id"]) === Number(teamId)
                            ? "ta-own-team"
                            : ""
                        }
                      >
                        {g[side + "_image_path"] && (
                          <img src={g[side + "_image_path"]} alt="" />
                        )}
                        {g[side + "_name"]}
                      </span>
                    </React.Fragment>
                  ))}
                </td>
                <td>
                  {number(g.goals?.home, en, 0)}−{number(g.goals?.away, en, 0)}
                </td>
                <td>
                  <Signal value={p.goals} en={en} />
                  <details><summary>{t("Modellwerte & Leistung", "Model values & performance")}</summary>
                    {[["result",t("Ergebnismodell", "Results model")],["statistics",t("Statistikmodell", "Statistics model")],["should","Should Goals"],["market",t("Marktrückrechnung", "Market reconstruction")]].map(([source,label])=>{
                      const summary=historySummary([g],teamId,source);
                      return <p key={source}>{label}: {summary.count ? `${number(summary.for,en)} − ${number(summary.against,en)}` : t("Daten fehlen","Data missing")}</p>;
                    })}
                  </details>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
function Signal({ value, en }) {
  return (
    <strong
      className={
        Number.isFinite(value)
          ? value > 0
            ? "ta-good"
            : value < 0
            ? "ta-bad"
            : ""
          : "ta-missing"
      }
    >
      {Number.isFinite(value) && value > 0 ? "+" : ""}
      {Number.isFinite(value)
        ? number(value, en)
        : en
        ? "Data missing"
        : "Daten fehlen"}
    </strong>
  );
}
export default function TeamPerformance({
  data,
  teamId,
  en = false,
  model = "result",
  recent = 8,
  venue = "all",
}) {
  const t = (d, e) => (en ? e : d);
  const [selectedModel, setSelectedModel] = useState(model);
  useEffect(() => setSelectedModel(model), [model]);
  return (
    <section className="ta-performance">
      <h3>
        {t(
          "Performance · Leistung zählt",
          "Performance · measured play matters"
        )}
      </h3>
      <label>
        {t("Vergleichsmodell", "Comparison model")}{" "}
        <select
          value={selectedModel}
          onChange={(e) => setSelectedModel(e.target.value)}
        >
          <option value="result">{t("Ergebnismodell", "Results model")}</option>
          <option value="statistics">
            {t("Statistikmodell", "Statistics model")}
          </option>
        </select>
      </label>
      <p>
        {t("Erwartung", "Expectation")}:{" "}
        {selectedModel === "result"
          ? t("Ergebnismodell", "Results model")
          : t("Statistikmodell", "Statistics model")}
      </p>
      <div className="ta-performance-periods">
        {[recent, 0].map((n) => {
          const p = performanceComparison(
            data.observations,
            teamId,
            selectedModel,
            {
              recent: n,
              venue,
            }
          );
          return (
            <article key={n}>
              <h4>
                {n
                  ? t("Letzte " + n + " Spiele", "Last " + n + " matches")
                  : t("Saison", "Season")}{" "}
                · {p.count} {t("Spiele", "matches")}
              </h4>
              <table>
                <thead>
                  <tr>
                    <th></th>
                    <th>
                      {t("Tordifferenz / Spiel", "Goal difference / game")}
                    </th>
                    <th>{t("Punkte / Spiel", "Points / game")}</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    [
                      "expected",
                      t("Leistung − Erwartung", "Performance − expectation"),
                    ],
                    [
                      "result",
                      t("Leistung − Ergebnis", "Performance − result"),
                    ],
                  ].map(([key, label]) => (
                    <tr key={key}>
                      <th>{label}</th>
                      <td>
                        <Signal value={p[key].goals} en={en} />
                        <small>
                          {" "}
                          · {p[key].goalCount}/{p.count}
                        </small>
                      </td>
                      <td>
                        <Signal value={p[key].points} en={en} />
                        <small>
                          {" "}
                          · {p[key].pointCount}/{p.count}
                        </small>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <details>
                <summary>{t("Spiele ansehen", "View matches")}</summary>
                <TeamMatches
                  games={p.games}
                  {...{ teamId, en }}
                  model={selectedModel}
                />
              </details>
            </article>
          );
        })}
      </div>
      <p>
        {t(
          "Leistung − Ergebnis: Plus bedeutet mehr verdient als erzielt (Ergebnispech), Minus mehr erzielt als verdient (Ergebnisglück). Ergebnisglück kann zur Überbewertung durch den Markt führen; Ergebnispech zur Unterbewertung. Ob der Markt dies bereits berücksichtigt, prüfen wir an den Quoten.",
          "Performance − result: positive means more deserved than achieved (unlucky results), negative means more achieved than deserved (lucky results). Lucky results may lead to market overvaluation, unlucky results to undervaluation. Market odds show whether this is already accounted for."
        )}
      </p>
    </section>
  );
}
